import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import VoiceLoop from "../components/VoiceLoop";
import SecureImg from "../components/SecureImg";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { compressImage } from "../lib/compressImage";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable,
  verticalListSortingStrategy, rectSortingStrategy, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import {
  Play, Square, Mic, Camera, MessageCircle, FileDown, Send,
  Clock, Image as ImgIcon, Sparkles, X, Loader2, StopCircle,
  Pencil, Wand2, Volume2, VolumeX, Trash2, Briefcase, CheckCircle2, Images,
} from "lucide-react";

const fmtMin = (m) => {
  const h = Math.floor((m || 0) / 60); const mm = (m || 0) % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

// Iter123k199 · Genius CoPilot · pagina mobile-first per sopralluogo
// Iter123k221 · Nota sortable (drag handle con GripVertical).
function SortableNota({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}
      data-testid={`copilot-nota-sortable-${id}`}>
      {typeof children === "function" ? children({ listeners }) : children}
    </div>
  );
}

// Iter123k221 · Foto sortable (drag su handle visibile in angolo).
function SortableFoto({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}
      data-testid={`copilot-foto-sortable-${id}`}>
      {typeof children === "function" ? children({ listeners }) : children}
    </div>
  );
}

// Iter123k225 · Riga Timeline sortable (chiave composita type:id).
function SortableTimelineRow({ uid, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: uid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}
      data-testid={`copilot-timeline-sortable-${uid}`}>
      {typeof children === "function" ? children({ listeners }) : children}
    </div>
  );
}


export default function CoPilotSopralluogo() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [sess, setSess] = useState(null);
  const [tab, setTab] = useState("note"); // note | chat | foto
  const [noteText, setNoteText] = useState("");
  const [chatText, setChatText] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [noteAdmin, setNoteAdmin] = useState("");
  // Iter123k252 · metadati "contesto relazione"
  const [personePresenti, setPersonePresenti] = useState("");
  const [finalitaSop, setFinalitaSop] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareEmails, setShareEmails] = useState("");
  const [shareCats, setShareCats] = useState(new Set());
  // Iter123k254 · liste destinatari arricchite + selezione per singolo soggetto
  const [shareLista, setShareLista] = useState({ fornitori: [], condomini: [], consiglieri: [] });
  const [shareListaLoading, setShareListaLoading] = useState(false);
  const [shareSoggSel, setShareSoggSel] = useState({ fornitori: new Set(), condomini: new Set(), consiglieri: new Set() });
  const [tick, setTick] = useState(0);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const [previewTesto, setPreviewTesto] = useState(""); // testo trascritto in attesa di conferma
  const [savingPreview, setSavingPreview] = useState(false);
  const [editingFoto, setEditingFoto] = useState(null); // {id, didascalia}
  const [fotoDidascalia, setFotoDidascalia] = useState("");
  const [savingFoto, setSavingFoto] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  // Strumenti AI post-sopralluogo
  const [qualita, setQualita] = useState(null); // {score, stato, mancanze, punti_forti, suggerimento}
  const [loadingQualita, setLoadingQualita] = useState(false);
  const [bozze, setBozze] = useState(null); // {preventivo, email_condomini, ticket}
  const [loadingBozze, setLoadingBozze] = useState(false);
  const [showBozze, setShowBozze] = useState(false);
  const [testoSemplice, setTestoSemplice] = useState("");
  const [loadingSemplice, setLoadingSemplice] = useState(false);
  const [showSemplice, setShowSemplice] = useState(false);
  const [showStima, setShowStima] = useState(false);
  const [stimaDesc, setStimaDesc] = useState("");
  const [stimaTesto, setStimaTesto] = useState("");
  const [loadingStima, setLoadingStima] = useState(false);
  // Voice Loop (hands-free continua + intervista revisione)
  const [voiceMode, setVoiceMode] = useState(null); // null | "chat" | "intervista"
  const [voceOn, setVoceOn] = useState(() => {
    try { return localStorage.getItem("copilot_voce_on") === "true"; }
    catch { return false; }
  });
  const [playingMsgId, setPlayingMsgId] = useState(null);
  // Eliminazione sopralluogo (solo admin)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Iter123k217 · Editor nota interattivo (click sulla card)
  const [editingNote, setEditingNote] = useState(null); // {id, testo, foto_ids}
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteAttachMode, setNoteAttachMode] = useState(false);
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(false);
  // Iter123k235 · trigger re-render dopo dismiss dell'hint drag timeline
  const [, setHintDismissTick] = useState(0);
  // Iter123k230 · Affida incarico a fornitore
  const [showAffida, setShowAffida] = useState(false);
  const [affidaFornitori, setAffidaFornitori] = useState([]); // elenco dal backend
  const [affidaSoggetti, setAffidaSoggetti] = useState([]); // elenco dal backend
  const [affidaForntSel, setAffidaForntSel] = useState(new Set()); // id selezionati
  const [affidaSoggSel, setAffidaSoggSel] = useState(new Set()); // id selezionati
  // Iter123k232 · filtri ricerca
  const [affidaForntFilter, setAffidaForntFilter] = useState("");
  const [affidaSoggFilter, setAffidaSoggFilter] = useState("");
  // Iter123k233 · inoltro opzionale del PDF ai condòmini selezionati
  const [affidaInoltraCond, setAffidaInoltraCond] = useState(false);
  const [affidaTipo, setAffidaTipo] = useState("ispettivo");
  const [affidaPriorita, setAffidaPriorita] = useState("Media");
  const [affidaNote, setAffidaNote] = useState("");
  const [affidaLoading, setAffidaLoading] = useState(false);
  const [affidaSending, setAffidaSending] = useState(false);
  const [affidaResult, setAffidaResult] = useState(null); // {tickets, inviati}
  // Iter123k231 · Collega condominio alla sessione (per sopralluoghi liberi con
  // solo condominio_nome e condominio_id null → sblocca "Affida incarico").
  const [showLinkCond, setShowLinkCond] = useState(false);
  const [linkCondList, setLinkCondList] = useState([]);
  const [linkCondFilter, setLinkCondFilter] = useState("");
  const [linkCondSel, setLinkCondSel] = useState("");
  const [linkCondLoading, setLinkCondLoading] = useState(false);
  const [linkCondSaving, setLinkCondSaving] = useState(false);
  // Iter123k243 · Riapri sessione CoPilot chiusa
  const [showReopen, setShowReopen] = useState(false);
  const [reopenMotivo, setReopenMotivo] = useState("");
  const [reopenSaving, setReopenSaving] = useState(false);
  const audioElRef = useRef(null);
  const fileInputFoto = useRef();
  const fileInputFotoGallery = useRef();
  const chatEndRef = useRef();
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recStartRef = useRef(0);
  const recTimerRef = useRef(null);

  const load = () => api.get(`/copilot/sessioni/${sid}`).then((r) => {
    setSess(r.data);
    setNoteAdmin(r.data.note_amministratore || "");
    setPersonePresenti(r.data.persone_presenti || "");
    setFinalitaSop(r.data.finalita || "");
  }).catch((e) => {
    toast.error(formatApiError(e.response?.data?.detail));
    navigate(-1);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);
  // cronometro live
  useEffect(() => {
    if (!sess || sess.stato === "chiusa") return;
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, [sess]);
  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [sess?.conversazione?.length, tab]);
  // Cleanup audio TTS su unmount
  useEffect(() => () => {
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
    }
  }, []);

  // Iter123k221 · drag & drop sensors (devono stare prima di qualsiasi early return)
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!sess) return <div className="p-6 text-center text-slate-500">Caricamento…</div>;

  const live = sess.stato !== "chiusa";
  // Iter123k243 · durata = minuti accumulati (cicli precedenti chiusi + riaperti) + porzione live corrente
  const durataAccumulata = Number(sess.durata_accumulata_min || 0);
  const durataMin = live
    ? durataAccumulata + Math.floor((Date.now() - new Date(sess.started_at).getTime()) / 60000)
    : (sess.durata_min || 0);
  // Iter123k253 · costo compenso sopralluogo rimosso dall'UI della relazione
  // (il valore resta persistito in sessione per dashboard/statistiche interne).

  const addNote = async () => {
    const t = noteText.trim(); if (!t) return;
    try { await api.post(`/copilot/sessioni/${sid}/note`, { testo: t, tipo: "manuale" }); setNoteText(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendChat = async () => {
    const t = chatText.trim(); if (!t || chatBusy) return;
    setChatBusy(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/chat`, { testo: t });
      setChatText("");
      // Autoplay TTS della risposta Ginius se voce attiva
      const agentMsg = r.data?.agent_msg;
      if (voceOn && agentMsg?.testo) {
        playTTS(agentMsg.id, agentMsg.testo);
      }
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setChatBusy(false);
  };

  // --- TTS (voce di Ginius) ---
  const toggleVoce = () => {
    const v = !voceOn;
    setVoceOn(v);
    try { localStorage.setItem("copilot_voce_on", v ? "true" : "false"); } catch { /* noop */ }
    if (!v && audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
      setPlayingMsgId(null);
    }
    toast.success(v ? "Voce di Ginius attivata" : "Voce di Ginius disattivata");
  };

  const playTTS = async (msgId, testo) => {
    if (!testo?.trim()) return;
    // Se sto già riproducendo questo messaggio → stop
    if (playingMsgId === msgId && audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
      setPlayingMsgId(null);
      return;
    }
    // Stop qualunque audio in corso
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* noop */ }
    }
    setPlayingMsgId(msgId);
    try {
      const r = await api.post("/copilot/tts", { testo }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "audio/mpeg" }));
      const audio = new Audio(url);
      audioElRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPlayingMsgId((cur) => (cur === msgId ? null : cur));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPlayingMsgId((cur) => (cur === msgId ? null : cur));
      };
      await audio.play();
    } catch (e) {
      setPlayingMsgId(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Riproduzione fallita");
    }
  };

  // Iter123k221 · handler persistenza riordino
  const onNoteDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const current = (sess?.note || []).slice()
      .sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9) || (a.at || "").localeCompare(b.at || ""));
    const oldIdx = current.findIndex((n) => n.id === active.id);
    const newIdx = current.findIndex((n) => n.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(current, oldIdx, newIdx);
    // Optimistic update
    setSess((prev) => prev ? {
      ...prev,
      note: reordered.map((n, i) => ({ ...n, order: i })),
    } : prev);
    try {
      await api.post(`/copilot/sessioni/${sid}/note/reorder`,
                       { ids: reordered.map((n) => n.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Riordino note fallito");
      load();
    }
  };
  const onFotoDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const current = (sess?.foto || []).slice()
      .sort((a, b) => (a.order ?? 1e9) - (b.order ?? 1e9) || (a.at || "").localeCompare(b.at || ""));
    const oldIdx = current.findIndex((f) => f.id === active.id);
    const newIdx = current.findIndex((f) => f.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(current, oldIdx, newIdx);
    setSess((prev) => prev ? {
      ...prev,
      foto: reordered.map((f, i) => ({ ...f, order: i })),
    } : prev);
    try {
      await api.post(`/copilot/sessioni/${sid}/foto/reorder`,
                       { ids: reordered.map((f) => f.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Riordino foto fallito");
      load();
    }
  };

  const uploadFoto = async (file) => {
    if (!file) return null;
    setUploadingFoto(true);
    let item = null;
    try {
      const fd = new FormData(); fd.append("file", await compressImage(file));
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.post(`/copilot/sessioni/${sid}/foto`,
                                 { file_id: up.data.id, kind: "foto", didascalia: "" });
      item = r.data?.item || null;
      toast.success("Foto aggiunta");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload fallito"); }
    setUploadingFoto(false);
    return item;
  };

  // Iter123k217 · gestione editor nota interattivo
  const openNoteEditor = (n) => {
    setEditingNote(n);
    setNoteDraft(n.testo || "");
    setNoteAttachMode(false);
    setConfirmDeleteNote(false);
  };
  const closeNoteEditor = () => {
    setEditingNote(null);
    setNoteDraft("");
    setNoteAttachMode(false);
    setConfirmDeleteNote(false);
  };
  const salvaNotaTesto = async () => {
    if (!editingNote) return;
    const testo = noteDraft.trim();
    if (!testo) { toast.error("Testo nota vuoto"); return; }
    if (testo === (editingNote.testo || "").trim()) return;  // niente da salvare
    setNoteSaving(true);
    try {
      await api.patch(`/copilot/sessioni/${sid}/note/${editingNote.id}`, { testo });
      toast.success("Nota aggiornata");
      await load();
      // allinea l'oggetto editing al nuovo testo (resta aperto)
      setEditingNote((prev) => prev ? { ...prev, testo, edited_at: new Date().toISOString() } : null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    }
    setNoteSaving(false);
  };
  const collegaFoto = async (fid) => {
    if (!editingNote) return;
    try {
      await api.post(`/copilot/sessioni/${sid}/note/${editingNote.id}/foto`, { foto_id: fid });
      toast.success("Foto collegata");
      await load();
      setEditingNote((prev) => prev ? {
        ...prev,
        foto_ids: Array.from(new Set([...(prev.foto_ids || []), fid])),
      } : null);
      setNoteAttachMode(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Collegamento fallito");
    }
  };
  const scollegaFoto = async (fid) => {
    if (!editingNote) return;
    try {
      await api.delete(`/copilot/sessioni/${sid}/note/${editingNote.id}/foto/${fid}`);
      await load();
      setEditingNote((prev) => prev ? {
        ...prev,
        foto_ids: (prev.foto_ids || []).filter((x) => x !== fid),
      } : null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scollegamento fallito");
    }
  };
  const eliminaNota = async () => {
    if (!editingNote) return;
    setNoteSaving(true);
    try {
      await api.delete(`/copilot/sessioni/${sid}/note/${editingNote.id}`);
      toast.success("Nota eliminata");
      await load();
      closeNoteEditor();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita");
    }
    setNoteSaving(false);
  };

  const endSession = async () => {
    if (!window.confirm("Chiudere il sopralluogo? Il cronometro si fermerà e verrà generato il report.")) return;
    try {
      await api.post(`/copilot/sessioni/${sid}/end`, { note_amministratore: noteAdmin });
      toast.success("Sopralluogo chiuso. Puoi scaricare o condividere il report.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const endSessionWithAI = async () => {
    const hasContent = (sess.note || []).length > 0 || (sess.conversazione || []).length > 0;
    if (!hasContent) {
      toast.error("Nessuna nota o conversazione: aggiungi almeno una nota prima di generare il riepilogo");
      return;
    }
    if (!window.confirm("Chiudere il sopralluogo e generare automaticamente il riepilogo con AI?")) return;
    setGeneratingSummary(true);
    try {
      // 1) Genera riepilogo AI
      const r = await api.post(`/copilot/sessioni/${sid}/riepilogo-ai`);
      const testo = r.data?.testo || "";
      if (!testo) throw new Error("Riepilogo vuoto");
      // 2) Chiudi sessione con note_amministratore = riepilogo
      // (se l'utente aveva già scritto qualcosa in noteAdmin, lo prepongo)
      const finale = noteAdmin.trim()
        ? `${noteAdmin.trim()}\n\n— — —\n\n${testo}`
        : testo;
      await api.post(`/copilot/sessioni/${sid}/end`, { note_amministratore: finale });
      setNoteAdmin(finale);
      toast.success(`Sopralluogo chiuso con riepilogo AI (${r.data.basato_su_note} note analizzate)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Chiusura con AI fallita");
    }
    setGeneratingSummary(false);
  };

  // Iter123k243 · Riapri sessione chiusa (admin tenant o tecnico creatore)
  const canReopen =
    sess?.stato === "chiusa" &&
    (isAdmin || sess?.operatore_id === user?.id);
  const openReopen = () => { setReopenMotivo(""); setShowReopen(true); };
  const confirmReopen = async () => {
    setReopenSaving(true);
    try {
      await api.post(`/copilot/sessioni/${sid}/reopen`,
                      { motivo: reopenMotivo.trim() });
      toast.success("Sopralluogo riaperto: il cronometro è ripartito. La durata precedente è conservata.");
      setShowReopen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Riapertura non riuscita");
    }
    setReopenSaving(false);
  };

  // Iter123k214 · Persiste le note amministratore su backend (chiamata idempotente).
  // Serve perché /end le salva solo alla chiusura: successive modifiche o riepilogo AI
  // generato dopo la chiusura resterebbero solo locali e NON finirebbero in PDF/DOCX.
  const persistNoteAdmin = async (testo) => {
    try {
      await api.patch(`/copilot/sessioni/${sid}/note-amministratore`,
                       { note_amministratore: testo });
      return true;
    } catch (_e) { return false; }
  };

  // Iter123k252 · persiste persone presenti / finalità (debounced via onBlur)
  const persistMetadati = async (patch) => {
    try {
      await api.patch(`/copilot/sessioni/${sid}/metadati`, patch);
      return true;
    } catch (_e) { return false; }
  };

  const downloadPdf = async () => {
    try {
      // Prima salvo sul backend il testo corrente (anche vuoto): così il PDF
      // include SEMPRE la versione visibile a video.
      if (sess && sess.stato === "chiusa") await persistNoteAdmin(noteAdmin);
      const r = await api.get(`/copilot/sessioni/${sid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `sopralluogo_${sid.slice(0, 8)}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download fallito"); }
  };

  const downloadDocx = async () => {
    try {
      if (sess && sess.stato === "chiusa") await persistNoteAdmin(noteAdmin);
      const r = await api.get(`/copilot/sessioni/${sid}/docx`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      }));
      const a = document.createElement("a"); a.href = url;
      a.download = `sopralluogo_${sid.slice(0, 8)}.docx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download DOCX fallito"); }
  };

  const eliminaSopralluogo = async () => {
    setDeleting(true);
    try {
      await api.delete(`/copilot/sessioni/${sid}`);
      toast.success("Sopralluogo eliminato");
      navigate("/copilot/mappa");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) {
        toast.error("Solo l'admin dello studio può eliminare i sopralluoghi");
      } else if (code === 404) {
        toast.error("Sopralluogo non trovato");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita");
      }
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // --- Registrazione vocale → Whisper ---
  const startRecording = async () => {
    if (recording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microfono non supportato dal browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1500) {
          toast.error("Registrazione troppo breve");
          return;
        }
        await transcribeAndSave(blob, mr.mimeType);
      };
      mr.start();
      mediaRecRef.current = mr;
      recStartRef.current = Date.now();
      setRecording(true);
      setRecElapsed(0);
      recTimerRef.current = setInterval(() => {
        setRecElapsed(Math.floor((Date.now() - recStartRef.current) / 1000));
      }, 500);
    } catch (e) {
      toast.error("Permesso microfono negato");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  const transcribeAndSave = async (blob, mimeType) => {
    setTranscribing(true);
    try {
      const ext = (mimeType || "").includes("mp4") ? "mp4"
                : (mimeType || "").includes("wav") ? "wav"
                : "webm";
      const fd = new FormData();
      fd.append("file", blob, `nota.${ext}`);
      fd.append("save_as_note", "false"); // preview mode: l'utente conferma
      const r = await api.post(`/copilot/sessioni/${sid}/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const testo = (r.data?.testo || "").trim();
      if (!testo) {
        toast.error("Nessun testo rilevato nell'audio");
      } else {
        setPreviewTesto(testo);
        toast.success("Trascrizione pronta: correggi e salva");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Trascrizione fallita");
    }
    setTranscribing(false);
  };

  const confermaPreview = async () => {
    const t = previewTesto.trim();
    if (!t) { setPreviewTesto(""); return; }
    setSavingPreview(true);
    try {
      await api.post(`/copilot/sessioni/${sid}/note`, { testo: t, tipo: "vocale" });
      toast.success("Nota vocale salvata");
      setPreviewTesto("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    }
    setSavingPreview(false);
  };

  const scartaPreview = () => {
    if (previewTesto && !window.confirm("Scartare la trascrizione? Il testo andrà perso.")) return;
    setPreviewTesto("");
  };

  // ---- Strumenti AI (revisione report) ----
  const copyToClipboard = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copiato"); }
    catch { toast.error("Copia non disponibile"); }
  };

  const valutaQualita = async () => {
    setLoadingQualita(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/qualita`);
      setQualita(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Valutazione fallita");
    }
    setLoadingQualita(false);
  };

  const generaBozze = async () => {
    setLoadingBozze(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/prossimi-passi`);
      setBozze(r.data.bozze);
      setShowBozze(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bozze non disponibili");
    }
    setLoadingBozze(false);
  };

  const generaSemplice = async () => {
    setLoadingSemplice(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/spiegamelo-semplice`);
      setTestoSemplice(r.data.testo);
      setShowSemplice(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Testo non disponibile");
    }
    setLoadingSemplice(false);
  };

  const generaStima = async () => {
    setLoadingStima(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/stima-costi`,
        { descrizione_libera: stimaDesc.trim() || undefined });
      setStimaTesto(r.data.testo);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Stima non disponibile");
    }
    setLoadingStima(false);
  };

  // --- Didascalia foto ---
  const openFotoEditor = (foto) => {
    setEditingFoto(foto);
    setFotoDidascalia(foto.didascalia || "");
  };

  // Iter123k222 · Crea una nota rapida a partire da una foto e la sua didascalia.
  // Usa il POST /note esteso con foto_ids per collegamento in un unico colpo.
  const creaNotaDaFoto = async (fotoId, testo) => {
    const t = (testo || "").trim();
    if (!t) { toast.error("Didascalia vuota: scrivila o generala con l'AI"); return; }
    try {
      await api.post(`/copilot/sessioni/${sid}/note`,
                       { testo: t, tipo: "manuale", foto_ids: [fotoId] });
      toast.success("Nota creata e collegata alla foto");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Creazione nota fallita");
    }
  };

  const salvaDidascaliaFoto = async () => {
    if (!editingFoto) return;
    setSavingFoto(true);
    try {
      await api.patch(`/copilot/sessioni/${sid}/foto/${editingFoto.id}`,
        { didascalia: fotoDidascalia });
      toast.success("Didascalia salvata");
      setEditingFoto(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    }
    setSavingFoto(false);
  };

  // --- Riepilogo AI finale ---
  const generaRiepilogoAI = async () => {
    if (generatingSummary) return;
    if (noteAdmin.trim() &&
        !window.confirm("Le Note Amministratore contengono già del testo. Sostituirle con il riepilogo generato dall'AI?")) {
      return;
    }
    setGeneratingSummary(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/riepilogo-ai`);
      if (r.data?.testo) {
        setNoteAdmin(r.data.testo);
        // Persisto subito: così PDF/DOCX scaricati immediatamente lo includono.
        if (sess?.stato === "chiusa") await persistNoteAdmin(r.data.testo);
        toast.success(`Relazione generata (${r.data.basato_su_note || 0} note, ${r.data.basato_su_foto || 0} foto, ${r.data.basato_su_messaggi || 0} msg)`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Riepilogo non disponibile");
    }
    setGeneratingSummary(false);
  };

  const toggleCat = (c) => { const n = new Set(shareCats); n.has(c) ? n.delete(c) : n.add(c); setShareCats(n); };

  // Iter123k254 · apre il dialog Condividi report e carica la lista arricchita.
  const openShareDialog = async () => {
    setSharing(true);
    setShareEmails(""); setShareCats(new Set());
    setShareSoggSel({ fornitori: new Set(), condomini: new Set(), consiglieri: new Set() });
    setShareListaLoading(true);
    try {
      const r = await api.get(`/copilot/sessioni/${sid}/destinatari-disponibili`);
      setShareLista({
        fornitori: r.data?.fornitori || [],
        condomini: r.data?.condomini || [],
        consiglieri: r.data?.consiglieri || [],
      });
    } catch (_e) {
      setShareLista({ fornitori: [], condomini: [], consiglieri: [] });
    } finally { setShareListaLoading(false); }
  };

  // Iter123k254 · toggle di un singolo soggetto nella lista scelti
  const toggleSogg = (cat, id) => {
    const next = { ...shareSoggSel };
    const s = new Set(next[cat] || []);
    if (s.has(id)) s.delete(id); else s.add(id);
    next[cat] = s;
    setShareSoggSel(next);
  };
  const toggleAllCat = (cat) => {
    const next = { ...shareSoggSel };
    const all = shareLista[cat] || [];
    const currentlyAll = all.length > 0 && (next[cat]?.size || 0) === all.length;
    next[cat] = currentlyAll ? new Set() : new Set(all.map((x) => x.id));
    setShareSoggSel(next);
  };

  const inviaReport = async () => {
    const emailsManuali = shareEmails.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    // Email dei soggetti scelti puntualmente
    const emailsSogg = [];
    ["fornitori", "condomini", "consiglieri"].forEach((cat) => {
      const sel = shareSoggSel[cat] || new Set();
      (shareLista[cat] || []).forEach((x) => { if (sel.has(x.id) && x.email) emailsSogg.push(x.email); });
    });
    const emails = Array.from(new Set([...emailsManuali, ...emailsSogg]));
    if (emails.length === 0 && shareCats.size === 0) {
      toast.error("Seleziona almeno un destinatario (soggetto, categoria o email)"); return;
    }
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/invia`,
        { emails, destinatari_categorie: Array.from(shareCats) });
      toast.success(`Report inviato a ${r.data.inviati} destinatari`);
      setSharing(false); setShareEmails(""); setShareCats(new Set());
      setShareSoggSel({ fornitori: new Set(), condomini: new Set(), consiglieri: new Set() });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
  };

  // Iter123k230 · Affida incarico (apertura dialog + load dati)
  const openAffida = async () => {
    if (!sess?.condominio_id) {
      toast.error("Imposta il condominio del sopralluogo prima di affidare l'incarico");
      return;
    }
    setShowAffida(true);
    setAffidaResult(null);
    setAffidaForntSel(new Set()); setAffidaSoggSel(new Set());
    setAffidaForntFilter(""); setAffidaSoggFilter("");
    setAffidaInoltraCond(false);
    setAffidaTipo("ispettivo"); setAffidaPriorita("Media"); setAffidaNote("");
    setAffidaLoading(true);
    setAffidaLoading(true);
    try {
      const [forn, sogg] = await Promise.all([
        api.get(`/fornitori`, { params: { limit: 1000 } }),
        api.get(`/condomini/${sess.condominio_id}/condomini-con-account`,
          { params: { include_anagrafica: true } }),
      ]);
      // /api/fornitori returns { fornitori: [...] } (paginated)
      setAffidaFornitori(Array.isArray(forn.data) ? forn.data : (forn.data?.fornitori || []));
      setAffidaSoggetti(sogg.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caricamento dati fallito");
    } finally { setAffidaLoading(false); }
  };

  const toggleAffidaForn = (id) => {
    const n = new Set(affidaForntSel);
    n.has(id) ? n.delete(id) : n.add(id);
    setAffidaForntSel(n);
  };
  const toggleAffidaSogg = (id) => {
    const n = new Set(affidaSoggSel);
    n.has(id) ? n.delete(id) : n.add(id);
    setAffidaSoggSel(n);
  };

  const submitAffida = async () => {
    if (affidaForntSel.size === 0) {
      toast.error("Seleziona almeno un fornitore"); return;
    }
    if (affidaInoltraCond && affidaSoggSel.size === 0) {
      toast.error("Seleziona almeno un condòmino da inoltrare, oppure togli la spunta"); return;
    }
    setAffidaSending(true);
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/affida-incarico`, {
        fornitore_ids: Array.from(affidaForntSel),
        soggetto_ids: Array.from(affidaSoggSel),
        tipo_incarico: affidaTipo,
        priorita: affidaPriorita,
        note: affidaNote,
        inoltra_a_condomini: affidaInoltraCond,
      });
      setAffidaResult(r.data);
      const n = r.data.tickets?.length || 0;
      const ic = r.data.inoltrati_condomini || 0;
      const extra = affidaInoltraCond ? ` · Inoltrati ai condòmini: ${ic}` : "";
      toast.success(`${n} manutenzion${n === 1 ? "e creata" : "i create"} · Email fornitori: ${r.data.inviati}${extra}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Affidamento fallito");
    } finally { setAffidaSending(false); }
  };

  // Iter123k231 · Collega condominio (sopralluoghi liberi senza condominio_id)
  const openLinkCondominio = async () => {
    setShowLinkCond(true);
    // Non pre-filtriamo: il nome originale è già citato nell'intro del dialog e
    // di solito non coincide con alcun condominio in anagrafica, quindi un filtro
    // iniziale produrrebbe "nessun risultato" e confonderebbe l'utente.
    setLinkCondFilter("");
    setLinkCondSel("");
    setLinkCondLoading(true);
    try {
      const r = await api.get(`/copilot/condomini`);
      setLinkCondList(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caricamento condomini fallito");
    } finally { setLinkCondLoading(false); }
  };

  const submitLinkCondominio = async () => {
    if (!linkCondSel) {
      toast.error("Seleziona un condominio dall'elenco"); return;
    }
    setLinkCondSaving(true);
    try {
      await api.patch(`/copilot/sessioni/${sid}/condominio`, { condominio_id: linkCondSel });
      toast.success("Condominio collegato");
      setShowLinkCond(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Collegamento fallito");
    } finally { setLinkCondSaving(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="copilot-page">
      {voiceMode && (
        <VoiceLoop sid={sid} mode={voiceMode}
          onClose={() => { setVoiceMode(null); load(); }}
          onIntervistaResult={(resp) => {
            if (!resp?.length) return;
            const blocchi = resp.map(({ domanda, risposta }) => `**${domanda}**\n${risposta}`).join("\n\n");
            setNoteAdmin((prev) => prev ? `${prev}\n\n--- Intervista di revisione ---\n${blocchi}` : blocchi);
            toast.success("Intervista aggiunta alle Note Amministratore");
          }}
          onCapturePhoto={async (file) => { return await uploadFoto(file); }}
        />
      )}
      {/* Header fisso con cronometro + tariffa */}
      <div className="sticky top-0 z-20 bg-brand text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider opacity-80">Sopralluogo CoPilot</div>
            <div className="font-bold truncate">{sess.condominio_nome || "—"}</div>
            <div className="text-xs opacity-80 truncate">{sess.appalto_titolo}</div>
          </div>
          <button data-testid="copilot-close" onClick={() => navigate(-1)}
            className="rounded-full bg-white/15 p-2 hover:bg-white/25"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 gap-2 mt-3 text-sm">
          <div className="bg-white/10 rounded-lg px-2 py-1.5 flex items-center gap-1.5 justify-center">
            <Clock className="w-4 h-4" /> <b data-testid="copilot-durata">{fmtMin(durataMin)}</b>
          </div>
        </div>
        {!live && (
          <div className="mt-2 text-center text-xs bg-emerald-200 text-emerald-900 rounded-md py-1 font-semibold">
            Sopralluogo terminato · {fmtMin(sess.durata_min)}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-white px-2">
        {[
          ["note", "Note", MessageCircle],
          ["chat", "CoPilot", Sparkles],
          ["foto", "Foto", ImgIcon],
          ["timeline", "Timeline", Clock],
        ].map(([k, l, I]) => (
          <button key={k} data-testid={`copilot-tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 py-3 flex items-center justify-center gap-1 text-sm font-bold border-b-[3px] ${tab === k ? "border-brand text-brand" : "border-transparent text-slate-500"}`}>
            <I className="w-4 h-4" /> {l}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="p-3 space-y-3">
        {tab === "note" && (
          <>
            <Card className="p-3" data-testid="copilot-nota-input-card">
              {!live && (
                <div className="text-[11px] rounded bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1 mb-2"
                  data-testid="copilot-nota-post-hint">
                  Aggiunta <b>post-sopralluogo</b>: la nota comparirà nel report (PDF/Word) con un'indicazione dedicata.
                </div>
              )}
              <textarea data-testid="copilot-nota-input" rows={3}
                value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder={live
                  ? "Scrivi una nota (es. muffa su parete nord ingresso)…"
                  : "Aggiungi una nota a posteriori (es. verifica eseguita il giorno dopo)…"}
                className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm resize-none focus:border-brand focus:outline-none" />
              <div className="flex gap-2 mt-2">
                <Button data-testid="copilot-nota-add" onClick={addNote} disabled={!noteText.trim()}
                  className="flex-1 bg-brand hover:bg-brand/90 text-white">
                  <Send className="w-4 h-4 mr-1" /> Aggiungi nota
                </Button>
                {!recording ? (
                  <Button data-testid="copilot-mic-start" onClick={startRecording}
                    disabled={transcribing}
                    title="Registra nota vocale (Whisper AI)"
                    className="bg-slate-700 hover:bg-slate-800 text-white px-3">
                    {transcribing
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Mic className="w-4 h-4" />}
                  </Button>
                ) : (
                  <Button data-testid="copilot-mic-stop" onClick={stopRecording}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 animate-pulse">
                    <Square className="w-4 h-4 mr-1" fill="currentColor" />
                      {recElapsed}s
                    </Button>
                  )}
                </div>
                {transcribing && (
                  <div data-testid="copilot-transcribing" className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Trascrizione in corso…
                  </div>
                )}
              </Card>
            {live && previewTesto && (
              <Card className="p-3 border-2 border-brand" data-testid="copilot-preview-card">
                <div className="flex items-center gap-1.5 text-xs font-bold text-brand uppercase mb-1.5">
                  <Mic className="w-3.5 h-3.5" /> Anteprima trascrizione · correggi se serve
                </div>
                <textarea data-testid="copilot-preview-input" rows={4}
                  value={previewTesto}
                  onChange={(e) => setPreviewTesto(e.target.value)}
                  className="w-full rounded-lg border-2 border-brand/40 p-2 text-sm resize-none focus:border-brand focus:outline-none" />
                <div className="flex gap-2 mt-2">
                  <Button data-testid="copilot-preview-discard" onClick={scartaPreview}
                    disabled={savingPreview}
                    className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800">
                    Scarta
                  </Button>
                  <Button data-testid="copilot-preview-save" onClick={confermaPreview}
                    disabled={savingPreview || !previewTesto.trim()}
                    className="flex-[2] bg-brand hover:bg-brand/90 text-white">
                    {savingPreview
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <Send className="w-4 h-4 mr-1" />}
                    Salva come nota vocale
                  </Button>
                </div>
              </Card>
            )}
            {(sess.note || []).length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-6">Nessuna nota ancora. {live ? "Inizia a documentare!" : ""}</div>
            ) : (() => {
              const sortedNote = (sess.note || []).slice().sort(
                (a, b) => (a.order ?? 1e9) - (b.order ?? 1e9)
                           || (a.at || "").localeCompare(b.at || ""),
              );
              return (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter}
                  onDragEnd={onNoteDragEnd}>
                  <SortableContext items={sortedNote.map((n) => n.id)}
                    strategy={verticalListSortingStrategy}>
                    <div className="space-y-2" data-testid="copilot-note-list">
                      {sortedNote.map((n) => {
                        const fotos = (n.foto_ids || [])
                          .map((fid) => (sess.foto || []).find((f) => f.id === fid))
                          .filter(Boolean);
                        return (
                          <SortableNota key={n.id} id={n.id}>
                            {({ listeners }) => (
                              <Card
                                data-testid={`copilot-nota-${n.id}`}
                                onClick={() => openNoteEditor(n)}
                                className="p-3 text-sm cursor-pointer hover:border-brand/40 hover:shadow-sm transition-all relative group">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-wrap">
                                    <button data-testid={`copilot-nota-handle-${n.id}`}
                                      {...listeners}
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label="Trascina per riordinare"
                                      className="cursor-grab active:cursor-grabbing touch-none p-0.5 -ml-0.5 text-slate-300 hover:text-slate-600">
                                      <GripVertical className="w-3.5 h-3.5" />
                                    </button>
                                    {(n.at || "").slice(11, 16)} · {n.tipo}
                                    {n.post_sessione && (
                                      <span data-testid={`copilot-nota-post-badge-${n.id}`}
                                        className="ml-1 px-1.5 py-[1px] rounded-full bg-amber-100 text-amber-800 text-[9px] font-semibold">
                                        post-sopralluogo
                                      </span>
                                    )}
                                    {n.edited_at && <span className="ml-1 text-amber-500">· modificata</span>}
                                  </div>
                                  <Pencil className="w-3 h-3 text-slate-300 group-hover:text-brand transition-colors" />
                                </div>
                                <div className="whitespace-pre-wrap">{n.testo}</div>
                                {fotos.length > 0 && (
                                  <div className="flex gap-1.5 mt-2 overflow-x-auto"
                                    data-testid={`copilot-nota-${n.id}-foto-strip`}>
                                    {fotos.map((f) => (
                                      <SecureImg key={f.id}
                                        path={`/files/${f.file_id}`}
                                        dataTestid={`copilot-nota-${n.id}-foto-${f.id}`}
                                        className="w-14 h-14 object-cover rounded border border-slate-200 shrink-0"
                                        placeholderClassName="w-14 h-14 rounded shrink-0" />
                                    ))}
                                  </div>
                                )}
                              </Card>
                            )}
                          </SortableNota>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              );
            })()}
          </>
        )}

        {tab === "chat" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-slate-500">Chat con Ginius · Claude Sonnet 5.5</div>
              <div className="flex gap-1">
                {live && (
                  <button data-testid="copilot-handsfree-btn" onClick={() => setVoiceMode("chat")}
                    className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border-2 bg-brand/10 text-brand border-brand hover:bg-brand hover:text-white">
                    <Mic className="w-3 h-3" /> Hands-free
                  </button>
                )}
                <button data-testid="copilot-voce-toggle" onClick={toggleVoce}
                  className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border-2 ${voceOn ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-300"}`}>
                  {voceOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                  {voceOn ? "Voce ON" : "Voce OFF"}
                </button>
              </div>
            </div>
            {!live && (
              <div data-testid="copilot-chat-chiusa-hint"
                className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1.5 mb-2">
                La sessione è chiusa: la chat hands-free è disponibile solo durante il sopralluogo.
                Puoi usare "Intervista di revisione" dagli Strumenti AI per aggiungere note extra.
              </div>
            )}
            <div className="space-y-2 pb-3">
              {(sess.conversazione || []).length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-6">
                  Chiedi un consiglio al CoPilot (es. "cosa dovrei verificare in un condominio con infiltrazioni?")
                </div>
              ) : (
                (sess.conversazione || []).map((m) => (
                  <div key={m.id} className={`flex items-end gap-1 ${m.ruolo === "utente" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.ruolo === "utente" ? "bg-brand text-white" : "bg-white border border-slate-200"}`}
                      data-testid={`copilot-msg-${m.ruolo}`}>
                      {m.testo}
                    </div>
                    {m.ruolo === "agente" && m.testo && (
                      <button data-testid={`copilot-msg-play-${m.id}`}
                        onClick={() => playTTS(m.id, m.testo)}
                        title={playingMsgId === m.id ? "Interrompi" : "Ascolta"}
                        className={`p-1.5 rounded-full border-2 shrink-0 ${playingMsgId === m.id ? "bg-brand text-white border-brand animate-pulse" : "bg-white text-slate-600 border-slate-300 hover:border-brand hover:text-brand"}`}>
                        {playingMsgId === m.id
                          ? <Square className="w-3 h-3" fill="currentColor" />
                          : <Volume2 className="w-3 h-3" />}
                      </button>
                    )}
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            {live && (
              <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t flex gap-2">
                <input data-testid="copilot-chat-input" value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
                  placeholder="Chiedi al CoPilot…"
                  className="flex-1 rounded-full border-2 border-slate-200 px-4 py-2 text-sm focus:border-brand focus:outline-none" />
                <Button data-testid="copilot-chat-send" onClick={sendChat} disabled={chatBusy}
                  className="rounded-full bg-brand hover:bg-brand/90 text-white px-3">
                  {chatBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </>
        )}

        {tab === "foto" && (
          <>
            {/* Iter123k242 · consentiamo upload foto anche a sessione CHIUSA
                (es. foto scattate dopo o importate dalla galleria/filesystem,
                utili per completare Timeline e Note). */}
            <Card className="p-3">
              <input ref={fileInputFoto} type="file" accept="image/*" capture="environment" className="hidden"
                data-testid="copilot-foto-input"
                onChange={(e) => { if (e.target.files[0]) uploadFoto(e.target.files[0]); e.target.value = ""; }} />
              <input ref={fileInputFotoGallery} type="file" accept="image/*" multiple className="hidden"
                data-testid="copilot-foto-input-gallery"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  for (const f of files) { await uploadFoto(f); }
                  e.target.value = "";
                }} />
              {!live && (
                <div className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-snug">
                  Sopralluogo chiuso: le foto caricate verranno segnate come <b>post-chiusura</b> e potrai usarle in Timeline e Note.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="copilot-foto-btn" onClick={() => fileInputFoto.current?.click()}
                  disabled={uploadingFoto}
                  className="bg-brand hover:bg-brand/90 text-white">
                  {uploadingFoto ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
                  Scatta foto
                </Button>
                <Button data-testid="copilot-foto-btn-gallery" onClick={() => fileInputFotoGallery.current?.click()}
                  disabled={uploadingFoto}
                  className="border-2 border-brand bg-white text-brand hover:bg-[#FBEEF3]">
                  {uploadingFoto ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Images className="w-4 h-4 mr-1" />}
                  Da galleria / file
                </Button>
              </div>
            </Card>
            {(() => {
              const sortedFoto = (sess.foto || []).slice().sort(
                (a, b) => (a.order ?? 1e9) - (b.order ?? 1e9)
                           || (a.at || "").localeCompare(b.at || ""),
              );
              if (sortedFoto.length === 0) {
                return (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2 text-center text-sm text-slate-400 py-6">Nessuna foto ancora.</div>
                  </div>
                );
              }
              return (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter}
                  onDragEnd={onFotoDragEnd}>
                  <SortableContext items={sortedFoto.map((f) => f.id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-2 gap-2" data-testid="copilot-foto-grid">
                      {sortedFoto.map((f) => (
                        <SortableFoto key={f.id} id={f.id}>
                          {({ listeners }) => (
                            <div className="relative aspect-square bg-slate-200 rounded-lg overflow-hidden group cursor-pointer"
                              data-testid={`copilot-foto-${f.id}`}
                              onClick={() => openFotoEditor(f)}>
                              <SecureImg
                                path={`/files/${f.file_id}`}
                                alt={f.didascalia || "foto"}
                                dataTestid={`copilot-foto-img-${f.id}`}
                                className="w-full h-full object-cover"
                                placeholderClassName="w-full h-full" />
                              {/* Drag handle (angolo alto-sx) */}
                              <button data-testid={`copilot-foto-handle-${f.id}`}
                                {...listeners}
                                onClick={(e) => e.stopPropagation()}
                                aria-label="Trascina per riordinare"
                                className="absolute top-1 left-1 bg-black/50 text-white rounded-full p-1 touch-none cursor-grab active:cursor-grabbing hover:bg-black/70">
                                <GripVertical className="w-3 h-3" />
                              </button>
                              <div className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Pencil className="w-3 h-3" />
                              </div>
                              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5">
                                {f.didascalia
                                  ? <span className="line-clamp-2">{f.didascalia}</span>
                                  : <span className="italic opacity-70">+ aggiungi didascalia</span>}
                              </div>
                            </div>
                          )}
                        </SortableFoto>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              );
            })()}
          </>
        )}

        {/* Iter123k223 · Timeline unificata del sopralluogo (note + foto in ordine
            cronologico, con icona per tipologia). Click apre il relativo editor.
            Iter123k225 · drag & drop per riordino unico, persistito sul backend. */}
        {tab === "timeline" && (() => {
          // Costruisco sequenza: timeline_order (se definito) + fallback cronologico
          const noteById = Object.fromEntries((sess.note || []).map((n) => [n.id, n]));
          const fotoById = Object.fromEntries((sess.foto || []).map((f) => [f.id, f]));
          const tlOrder = Array.isArray(sess.timeline_order) ? sess.timeline_order : [];
          const seenNotes = new Set(), seenFoto = new Set();
          const seq = [];
          tlOrder.forEach((it) => {
            if (it.type === "nota" && noteById[it.id] && !seenNotes.has(it.id)) {
              seq.push({ kind: "nota", id: it.id, item: noteById[it.id] });
              seenNotes.add(it.id);
            } else if (it.type === "foto" && fotoById[it.id] && !seenFoto.has(it.id)) {
              seq.push({ kind: "foto", id: it.id, item: fotoById[it.id] });
              seenFoto.add(it.id);
            }
          });
          // Append items non in tl_order ordinati per at
          const tail = [];
          (sess.note || []).forEach((n) => {
            if (!seenNotes.has(n.id)) tail.push({ kind: "nota", id: n.id, item: n, _at: n.at || "" });
          });
          (sess.foto || []).forEach((f) => {
            if (!seenFoto.has(f.id)) tail.push({ kind: "foto", id: f.id, item: f, _at: f.at || "" });
          });
          tail.sort((a, b) => (a._at || "").localeCompare(b._at || ""));
          tail.forEach(({ kind, id, item }) => seq.push({ kind, id, item }));

          if (seq.length === 0) {
            return (
              <div className="text-center text-sm text-slate-400 py-10"
                data-testid="copilot-timeline-empty">
                La timeline è vuota: aggiungi note o foto per vederle in ordine qui.
              </div>
            );
          }
          const uids = seq.map((s) => `${s.kind}:${s.id}`);
          const notaUsesFoto = (n, fid) => (n.foto_ids || []).includes(fid);
          return (
            <DndContext sensors={dndSensors} collisionDetection={closestCenter}
              onDragEnd={async ({ active, over }) => {
                if (!over || active.id === over.id) return;
                const oldIdx = uids.indexOf(active.id);
                const newIdx = uids.indexOf(over.id);
                if (oldIdx < 0 || newIdx < 0) return;
                const reordered = arrayMove(seq, oldIdx, newIdx);
                const serverPayload = reordered.map((s) => ({ type: s.kind, id: s.id }));
                // Optimistic update del timeline_order lato stato
                setSess((prev) => prev ? { ...prev, timeline_order: serverPayload } : prev);
                try {
                  await api.post(`/copilot/sessioni/${sid}/timeline/reorder`,
                                   { items: serverPayload });
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Riordino fallito");
                  load();
                }
              }}>
              <SortableContext items={uids} strategy={verticalListSortingStrategy}>
                <div className="relative pl-5" data-testid="copilot-timeline">
                  <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
                  {/* Iter123k235 · Hint drag & drop (dismissibile) */}
                  {!localStorage.getItem("copilot_tl_hint_dismissed") && seq.length >= 2 && (
                    <div
                      data-testid="copilot-timeline-drag-hint"
                      className="mb-3 flex items-start gap-2 rounded-lg bg-[#FBEEF3] border border-brand/30 px-3 py-2 text-xs text-[#5A1230]">
                      <GripVertical className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                      <div className="flex-1 leading-snug">
                        <b>Suggerimento</b>: tocca e trascina l&apos;icona a sinistra di ogni voce
                        per riordinare la timeline a piacimento.
                      </div>
                      <button
                        data-testid="copilot-timeline-drag-hint-close"
                        onClick={() => {
                          localStorage.setItem("copilot_tl_hint_dismissed", "1");
                          setHintDismissTick((v) => v + 1);
                        }}
                        className="text-brand/70 hover:text-brand p-0.5 -mr-1"
                        aria-label="Chiudi suggerimento">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                    {seq.map((entry) => (
                      <SortableTimelineRow key={`${entry.kind}:${entry.id}`}
                        uid={`${entry.kind}:${entry.id}`}>
                        {({ listeners }) => {
                          if (entry.kind === "nota") {
                            const n = entry.item;
                            const Icon = n.tipo === "vocale" ? Mic : MessageCircle;
                            const dotColor = n.tipo === "vocale" ? "bg-emerald-500" : "bg-brand";
                            const linkedFotos = (n.foto_ids || [])
                              .map((fid) => fotoById[fid]).filter(Boolean);
                            const time = (n.at || "").slice(11, 16);
                            return (
                              <div className="relative"
                                data-testid={`copilot-timeline-item-n-${n.id}`}>
                                <div className={`absolute -left-5 top-2 w-4 h-4 rounded-full ${dotColor} flex items-center justify-center ring-2 ring-white`}>
                                  <Icon className="w-2.5 h-2.5 text-white" />
                                </div>
                                <Card
                                  onClick={() => openNoteEditor(n)}
                                  className="p-2.5 text-sm cursor-pointer hover:border-brand/40 hover:shadow-sm transition-all">
                                  <div className="flex items-center gap-1 mb-1">
                                    <button data-testid={`copilot-timeline-handle-n-${n.id}`}
                                      {...listeners}
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label="Trascina per riordinare"
                                      title="Trascina su/giù per riordinare"
                                      className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1 rounded-md text-brand/70 hover:text-brand hover:bg-[#FBEEF3] active:bg-[#F6D4DF]">
                                      <GripVertical className="w-5 h-5" />
                                    </button>
                                    <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-wrap">
                                      {time} · {n.tipo === "vocale" ? "nota vocale" : "nota manuale"}
                                      {n.post_sessione && (
                                        <span className="ml-1 px-1.5 py-[1px] rounded-full bg-amber-100 text-amber-800 text-[9px] font-semibold">post-sopralluogo</span>
                                      )}
                                      {n.edited_at && <span className="ml-1 text-amber-500">· modificata</span>}
                                    </div>
                                  </div>
                                  <div className="whitespace-pre-wrap">{n.testo}</div>
                                  {linkedFotos.length > 0 && (
                                    <div className="flex gap-1 mt-1.5 overflow-x-auto">
                                      {linkedFotos.map((f) => (
                                        <SecureImg key={f.id}
                                          path={`/files/${f.file_id}`}
                                          dataTestid={`copilot-timeline-nota-${n.id}-foto-${f.id}`}
                                          className="w-10 h-10 object-cover rounded border border-slate-200 shrink-0"
                                          placeholderClassName="w-10 h-10 rounded shrink-0" />
                                      ))}
                                    </div>
                                  )}
                                </Card>
                              </div>
                            );
                          }
                          // foto
                          const f = entry.item;
                          const noteConCollegamento = (sess.note || [])
                            .filter((n) => notaUsesFoto(n, f.id)).length;
                          const time = (f.at || "").slice(11, 16);
                          return (
                            <div className="relative"
                              data-testid={`copilot-timeline-item-f-${f.id}`}>
                              <div className="absolute -left-5 top-2 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center ring-2 ring-white">
                                <Camera className="w-2.5 h-2.5 text-white" />
                              </div>
                              <Card
                                onClick={() => openFotoEditor(f)}
                                className="p-2.5 text-sm cursor-pointer hover:border-brand/40 hover:shadow-sm transition-all">
                                <div className="flex items-start gap-2.5">
                                  <button data-testid={`copilot-timeline-handle-f-${f.id}`}
                                    {...listeners}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Trascina per riordinare"
                                    title="Trascina su/giù per riordinare"
                                    className="cursor-grab active:cursor-grabbing touch-none p-1 mt-0.5 -ml-1 rounded-md text-brand/70 hover:text-brand hover:bg-[#FBEEF3] active:bg-[#F6D4DF] shrink-0">
                                    <GripVertical className="w-5 h-5" />
                                  </button>
                                  <SecureImg path={`/files/${f.file_id}`}
                                    dataTestid={`copilot-timeline-foto-img-${f.id}`}
                                    className="w-16 h-16 object-cover rounded border border-slate-200 shrink-0"
                                    placeholderClassName="w-16 h-16 rounded shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-[10px] text-slate-400 mb-1">
                                      {time} · foto
                                      {noteConCollegamento > 0 && (
                                        <span className="ml-1 text-brand">· collegata a {noteConCollegamento} nota{noteConCollegamento > 1 ? "e" : ""}</span>
                                      )}
                                    </div>
                                    <div className={`line-clamp-3 ${f.didascalia ? "" : "italic text-slate-400"}`}>
                                      {f.didascalia || "+ aggiungi didascalia"}
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          );
                        }}
                      </SortableTimelineRow>
                    ))}
                  </div>
                  <div className="mt-3 text-[10px] text-slate-400 text-center"
                    data-testid="copilot-timeline-hint">
                    Trascina dall'icona ⋮⋮ per riordinare · il PDF/Word seguiranno questa sequenza
                  </div>
                </div>
              </SortableContext>
            </DndContext>
          );
        })()}

        {/* Note Amministratore + Report */}
        {!live && (
          <>
            <Card className="p-3 mt-2">
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="text-xs font-bold uppercase text-slate-500">Relazione tecnica (nel report PDF)</div>
                <div className="flex gap-1">
                  {noteAdmin.trim() && (
                    <button data-testid="copilot-note-admin-play"
                      onClick={() => playTTS("note-admin", noteAdmin)}
                      title={playingMsgId === "note-admin" ? "Interrompi lettura" : "Ascolta le note"}
                      className={`p-1 rounded-full border-2 ${playingMsgId === "note-admin" ? "bg-brand text-white border-brand animate-pulse" : "bg-white text-slate-600 border-slate-300 hover:border-brand hover:text-brand"}`}>
                      {playingMsgId === "note-admin"
                        ? <Square className="w-3 h-3" fill="currentColor" />
                        : <Volume2 className="w-3 h-3" />}
                    </button>
                  )}
                  <Button data-testid="copilot-ai-summary-btn" onClick={generaRiepilogoAI}
                    disabled={generatingSummary || ((sess.note || []).length === 0 && (sess.conversazione || []).length === 0 && (sess.foto || []).length === 0)}
                    size="sm"
                    className="h-7 bg-gradient-to-r from-purple-600 to-brand hover:opacity-90 text-white text-xs px-2">
                    {generatingSummary
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                    Relazione AI
                  </Button>
                </div>
              </div>
              <textarea data-testid="copilot-note-admin" rows={6}
                value={noteAdmin} onChange={(e) => setNoteAdmin(e.target.value)}
                onBlur={() => { if (sess?.stato === "chiusa") persistNoteAdmin(noteAdmin); }}
                placeholder="Osservazioni finali, decisioni, prossimi step… (oppure tap su 'Riepilogo AI' per pre-compilare)"
                className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
              {/* Iter123k252 · metadati contestuali usati dalla relazione AI */}
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-0.5">
                    Finalità del sopralluogo
                  </label>
                  <input data-testid="copilot-finalita-input" type="text"
                    value={finalitaSop}
                    onChange={(e) => setFinalitaSop(e.target.value)}
                    onBlur={() => persistMetadati({ finalita: finalitaSop })}
                    placeholder={sess?.appalto_titolo ? `es. ${sess.appalto_titolo}` : "es. verifica infiltrazioni ultimo piano"}
                    className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-0.5">
                    Persone presenti
                  </label>
                  <input data-testid="copilot-presenti-input" type="text"
                    value={personePresenti}
                    onChange={(e) => setPersonePresenti(e.target.value)}
                    onBlur={() => persistMetadati({ persone_presenti: personePresenti })}
                    placeholder="es. Sig. Rossi (consigliere), geom. Bianchi"
                    className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
                </div>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">
                Questi dati compaiono nell'introduzione della relazione AI e nel PDF.
              </div>
            </Card>

            {/* Strumenti AI per la revisione del report */}
            <Card className="p-3 mt-2" data-testid="copilot-ai-tools">
              <div className="text-xs font-bold uppercase text-slate-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Strumenti AI
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="ai-qualita-btn" onClick={valutaQualita}
                  disabled={loadingQualita}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9">
                  {loadingQualita ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Qualità report"}
                </Button>
                <Button data-testid="ai-passi-btn" onClick={generaBozze}
                  disabled={loadingBozze}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9">
                  {loadingBozze ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Prossimi passi"}
                </Button>
                <Button data-testid="ai-semplice-btn" onClick={generaSemplice}
                  disabled={loadingSemplice || !noteAdmin.trim()}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9">
                  {loadingSemplice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Spiega semplice"}
                </Button>
                <Button data-testid="ai-stima-btn" onClick={() => { setShowStima(true); setStimaTesto(""); }}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs h-9">
                  Stima costi DEI
                </Button>
                <Button data-testid="ai-intervista-btn" onClick={() => setVoiceMode("intervista")}
                  className="col-span-2 bg-gradient-to-r from-purple-600 to-brand hover:opacity-90 text-white text-xs h-9">
                  <Mic className="w-3.5 h-3.5 mr-1" /> Intervista vocale di revisione
                </Button>
              </div>

              {/* Semaforo qualità in-place */}
              {qualita && (
                <div data-testid="ai-qualita-result" className="mt-3 p-2 rounded-lg border-2"
                  style={{
                    borderColor: qualita.stato === "verde" ? "#059669" : qualita.stato === "giallo" ? "#eab308" : "#dc2626",
                    background: qualita.stato === "verde" ? "#ecfdf5" : qualita.stato === "giallo" ? "#fefce8" : "#fef2f2",
                  }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-sm">Qualità report: {qualita.score}/100</div>
                    <span className="text-[11px] uppercase font-bold tracking-wide">{qualita.stato}</span>
                  </div>
                  {qualita.suggerimento && (
                    <div className="text-xs text-slate-700 italic mb-1">💡 {qualita.suggerimento}</div>
                  )}
                  {qualita.mancanze?.length > 0 && (
                    <ul className="text-xs text-slate-700 list-disc list-inside">
                      {qualita.mancanze.map((m, i) => (<li key={i}>{m}</li>))}
                    </ul>
                  )}
                </div>
              )}
            </Card>
            <Card className="p-3">
              <Button data-testid="copilot-pdf-btn" onClick={downloadPdf}
                className="w-full bg-slate-800 hover:bg-slate-900 text-white mb-2">
                <FileDown className="w-4 h-4 mr-1" /> Scarica Report PDF
              </Button>
              <Button data-testid="copilot-docx-btn" onClick={downloadDocx}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white mb-2">
                <FileDown className="w-4 h-4 mr-1" /> Scarica Report DOCX (editabile)
              </Button>
              <Button data-testid="copilot-share-btn" onClick={openShareDialog}
                className="w-full border-2 border-brand bg-white text-brand hover:bg-[#FBEEF3]">
                <Send className="w-4 h-4 mr-1" /> Condividi via email
              </Button>
              {isAdmin && sess?.condominio_id && (
                <Button data-testid="copilot-affida-btn" onClick={openAffida}
                  className="w-full mt-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white">
                  <Briefcase className="w-4 h-4 mr-1" /> Affida incarico a Fornitore
                </Button>
              )}
              {isAdmin && !sess?.condominio_id && (
                <div data-testid="copilot-link-cond-alert"
                  className="mt-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-3 text-xs">
                  <div className="font-bold text-amber-900 mb-1 flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5" />
                    Collega il condominio per sbloccare l&apos;Affida incarico
                  </div>
                  <div className="text-amber-800 leading-snug mb-2">
                    Questo sopralluogo ha solo il <b>nome</b> del condominio (&laquo;{sess?.condominio_nome || "—"}&raquo;)
                    ma non è associato a un condominio in anagrafica.
                    Senza collegamento non si può affidare l&apos;incarico ai fornitori.
                  </div>
                  <Button data-testid="copilot-link-cond-btn" onClick={openLinkCondominio}
                    className="w-full h-9 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                    Collega a un condominio in anagrafica
                  </Button>
                </div>
              )}
              {/* Iter123k243 · Riapri sessione (admin tenant o tecnico creatore) */}
              {canReopen && (
                <div className="mt-3 rounded-lg border-2 border-sky-200 bg-sky-50 p-3 text-xs"
                  data-testid="copilot-riapri-card">
                  <div className="font-bold text-sky-900 mb-1 flex items-center gap-1">
                    <Play className="w-3.5 h-3.5" />
                    Torna sul campo
                  </div>
                  <div className="text-sky-800 leading-snug mb-2">
                    Riapri temporaneamente il sopralluogo per registrare nuova
                    durata e aggiungere foto/note come se fossi ancora sul campo.
                    La durata già registrata ({fmtMin(sess?.durata_min || 0)}) viene conservata e sommata.
                  </div>
                  <Button data-testid="copilot-riapri-btn" onClick={openReopen}
                    className="w-full h-9 bg-sky-600 hover:bg-sky-700 text-white text-xs">
                    <Play className="w-3.5 h-3.5 mr-1" /> Riapri sessione
                  </Button>
                </div>
              )}
            </Card>
            {isAdmin && (
              <Card className="p-3 mt-2 border-2 border-red-100 bg-red-50/40"
                data-testid="copilot-elimina-card">
                <div className="text-xs text-red-900 font-semibold mb-1">
                  Zona pericolosa
                </div>
                <div className="text-xs text-slate-600 mb-2">
                  Elimina questo sopralluogo (prova, errato o duplicato). Operazione irreversibile.
                </div>
                {confirmDelete ? (
                  <div className="flex gap-2">
                    <Button
                      data-testid="copilot-elimina-confirm"
                      onClick={eliminaSopralluogo}
                      disabled={deleting}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                      {deleting
                        ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        : <Trash2 className="w-4 h-4 mr-1" />}
                      Conferma eliminazione
                    </Button>
                    <Button
                      data-testid="copilot-elimina-cancel"
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                      variant="outline"
                      className="flex-none">
                      Annulla
                    </Button>
                  </div>
                ) : (
                  <Button
                    data-testid="copilot-elimina-btn"
                    onClick={() => setConfirmDelete(true)}
                    variant="outline"
                    className="w-full border-red-300 text-red-700 hover:bg-red-100">
                    <Trash2 className="w-4 h-4 mr-1" /> Elimina sopralluogo
                  </Button>
                )}
              </Card>
            )}
          </>
        )}
      </div>

      {/* Action bar fissa (solo durante sessione, tab != chat) */}
      {live && tab !== "chat" && (
        <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t flex gap-2 shadow-lg">
          <Button data-testid="copilot-end-ai-btn" onClick={endSessionWithAI}
            disabled={generatingSummary}
            className="flex-[2] bg-gradient-to-r from-purple-600 to-brand hover:opacity-90 text-white">
            {generatingSummary
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <Wand2 className="w-4 h-4 mr-1" />}
            Chiudi e genera report AI
          </Button>
          <Button data-testid="copilot-end-btn" onClick={endSession}
            disabled={generatingSummary}
            className="flex-1 bg-slate-700 hover:bg-slate-800 text-white">
            <StopCircle className="w-4 h-4 mr-1" /> Termina
          </Button>
        </div>
      )}

      {/* Dialog didascalia foto */}
      {editingFoto && (
        <div className="fixed inset-0 bg-black/80 z-30 flex items-center justify-center p-4"
          onClick={() => !savingFoto && setEditingFoto(null)}>
          <Card className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}
            data-testid="copilot-foto-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg">Didascalia foto</div>
              <button onClick={() => !savingFoto && setEditingFoto(null)}
                className="p-1 hover:bg-slate-100 rounded-full"
                data-testid="copilot-foto-dialog-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden mb-3 flex items-center justify-center">
              <SecureImg
                path={`/files/${editingFoto.file_id}`}
                alt="anteprima"
                dataTestid="copilot-foto-dialog-img"
                className="max-h-60 object-contain"
                placeholderClassName="w-full h-60" />
            </div>
            <textarea data-testid="copilot-foto-didascalia-input" rows={3}
              value={fotoDidascalia} onChange={(e) => setFotoDidascalia(e.target.value)}
              placeholder="Es. muffa su parete nord ingresso, infiltrazione sotto balcone 3° piano…"
              className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
            <Button data-testid="copilot-foto-vision-btn"
              onClick={async () => {
                try {
                  const r = await api.post(`/copilot/sessioni/${sid}/foto/${editingFoto.id}/vision`);
                  if (r.data?.didascalia) {
                    // Iter123k251 · Accoda la descrizione AI alla nota esistente invece di sovrascriverla
                    setFotoDidascalia((prev) => {
                      const base = (prev || "").trim();
                      const aiText = String(r.data.didascalia || "").trim();
                      if (!base) return aiText;
                      if (!aiText) return prev;
                      return `${base}\n\n${aiText}`;
                    });
                    toast.success("Didascalia AI aggiunta — modifica se serve");
                  }
                } catch (e) {
                  toast.error(formatApiError(e.response?.data?.detail) || "Analisi fallita");
                }
              }}
              className="w-full mt-2 bg-gradient-to-r from-purple-600 to-brand hover:opacity-90 text-white text-xs h-9">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Scatta e descrivi (Claude Vision)
            </Button>
            {/* Iter123k222 · Crea una nota rapida con la didascalia della foto */}
            <Button data-testid="copilot-foto-crea-nota"
              onClick={() => creaNotaDaFoto(editingFoto.id, fotoDidascalia)}
              disabled={!live || !fotoDidascalia.trim() || savingFoto}
              variant="outline"
              className="w-full mt-2 border-brand/40 text-brand hover:bg-brand/5 text-xs h-9">
              <Pencil className="w-3.5 h-3.5 mr-1" />
              {live
                ? "Crea nota con questa didascalia"
                : "Note bloccate (sessione chiusa)"}
            </Button>
            <div className="flex gap-2 mt-3">
              <Button onClick={() => setEditingFoto(null)} disabled={savingFoto}
                className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300">Annulla</Button>
              <Button data-testid="copilot-foto-didascalia-save" onClick={salvaDidascaliaFoto}
                disabled={savingFoto}
                className="flex-1 bg-brand text-white hover:bg-brand/90">
                {savingFoto ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Salva
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Iter123k217 · Dialog editor nota (click sulla card nota) */}
      {editingNote && (() => {
        const linked = (editingNote.foto_ids || [])
          .map((fid) => (sess.foto || []).find((f) => f.id === fid))
          .filter(Boolean);
        const linkedSet = new Set(editingNote.foto_ids || []);
        const availableFotos = (sess.foto || []).filter((f) => !linkedSet.has(f.id));
        const unchanged = noteDraft.trim() === (editingNote.testo || "").trim();
        return (
          <div className="fixed inset-0 bg-black/80 z-30 flex items-center justify-center p-4"
            onClick={() => !noteSaving && closeNoteEditor()}
            data-testid="copilot-nota-dialog">
            <Card className="w-full max-w-md p-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-lg flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-brand" /> Modifica nota
                </div>
                <button onClick={() => !noteSaving && closeNoteEditor()}
                  className="p-1 hover:bg-slate-100 rounded-full"
                  data-testid="copilot-nota-dialog-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="text-[10px] text-slate-400 mb-1">
                {(editingNote.at || "").slice(11, 16)} · {editingNote.tipo}
                {editingNote.edited_at && <span className="ml-1 text-amber-500">· già modificata</span>}
              </div>
              <textarea data-testid="copilot-nota-editor"
                rows={5}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm mb-3" />

              {/* Galleria foto collegate */}
              <div className="text-xs font-semibold text-slate-600 mb-1">
                Foto collegate ({linked.length})
              </div>
              {linked.length === 0 ? (
                <div className="text-xs text-slate-400 italic mb-2">
                  Nessuna foto collegata · tocca "Collega foto" per associarne una
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {linked.map((f) => (
                    <div key={f.id} className="relative"
                      data-testid={`copilot-nota-linked-${f.id}`}>
                      <SecureImg path={`/files/${f.file_id}`}
                        className="w-full aspect-square object-cover rounded border border-slate-200"
                        placeholderClassName="w-full aspect-square rounded" />
                      <button data-testid={`copilot-nota-unlink-${f.id}`}
                        onClick={() => scollegaFoto(f.id)}
                        title="Scollega foto"
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center shadow">
                        <X className="w-3 h-3" />
                      </button>
                      {f.didascalia && (
                        <div className="text-[9px] text-slate-500 line-clamp-2 mt-0.5">{f.didascalia}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Picker foto */}
              {!noteAttachMode ? (
                <Button data-testid="copilot-nota-attach-open"
                  onClick={() => setNoteAttachMode(true)}
                  disabled={availableFotos.length === 0}
                  variant="outline"
                  className="w-full border-brand/40 text-brand hover:bg-brand/5">
                  <ImgIcon className="w-4 h-4 mr-1" />
                  {availableFotos.length === 0
                    ? "Nessuna altra foto disponibile"
                    : `Collega foto dalla sezione Foto (${availableFotos.length})`}
                </Button>
              ) : (
                <div className="border-2 border-brand/30 rounded-lg p-2 mb-2 bg-brand/5"
                  data-testid="copilot-nota-attach-picker">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-brand">Scegli una foto</div>
                    <button data-testid="copilot-nota-attach-cancel"
                      onClick={() => setNoteAttachMode(false)}
                      className="text-xs text-slate-500 hover:text-slate-800">Annulla</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                    {availableFotos.map((f) => (
                      <button key={f.id}
                        data-testid={`copilot-nota-attach-${f.id}`}
                        onClick={() => collegaFoto(f.id)}
                        className="relative hover:ring-2 hover:ring-brand rounded">
                        <SecureImg path={`/files/${f.file_id}`}
                          className="w-full aspect-square object-cover rounded"
                          placeholderClassName="w-full aspect-square rounded" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Azioni testo */}
              <div className="flex gap-2 mt-3">
                {!confirmDeleteNote ? (
                  <Button data-testid="copilot-nota-delete-open"
                    onClick={() => setConfirmDeleteNote(true)}
                    disabled={noteSaving}
                    variant="outline"
                    className="flex-none border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button data-testid="copilot-nota-delete-confirm"
                    onClick={eliminaNota}
                    disabled={noteSaving}
                    className="flex-none bg-red-600 hover:bg-red-700 text-white">
                    {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                    Conferma
                  </Button>
                )}
                <Button data-testid="copilot-nota-save"
                  onClick={salvaNotaTesto}
                  disabled={noteSaving || unchanged}
                  className="flex-1 bg-brand text-white hover:bg-brand/90">
                  {noteSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {unchanged ? "Chiudi" : "Salva modifiche"}
                </Button>
              </div>
            </Card>
          </div>
        );
      })()}
      {showBozze && bozze && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => setShowBozze(false)}>
          <Card className="w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="ai-bozze-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg">3 Bozze pronte</div>
              <button onClick={() => setShowBozze(false)} className="p-1 hover:bg-slate-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            {["preventivo", "email_condomini", "ticket"].map((k) => {
              const b = bozze[k]; if (!b) return null;
              const label = k === "preventivo" ? "📋 Richiesta Preventivo" : k === "email_condomini" ? "✉️ Email ai Condomini" : "🎫 Ticket Manutenzione";
              const corpoKey = k === "ticket" ? "descrizione" : "corpo";
              const oggKey = k === "ticket" ? "titolo" : "oggetto";
              return (
                <div key={k} className="mb-3 p-2 border rounded-lg">
                  <div className="text-xs font-bold uppercase text-slate-500">{label}</div>
                  <div className="font-semibold text-sm mt-1">{b[oggKey]}</div>
                  <textarea rows={5} value={b[corpoKey]}
                    onChange={(e) => setBozze({ ...bozze, [k]: { ...b, [corpoKey]: e.target.value } })}
                    className="w-full mt-1 rounded border-2 border-slate-200 p-2 text-xs" />
                  <Button size="sm" onClick={() => copyToClipboard(`${b[oggKey]}\n\n${b[corpoKey]}`)}
                    className="mt-1 h-7 text-xs bg-brand text-white hover:bg-brand/90">
                    Copia
                  </Button>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* Dialog spiegamelo semplice */}
      {showSemplice && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => setShowSemplice(false)}>
          <Card className="w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="ai-semplice-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg">Spiegato in semplice (per assemblea)</div>
              <button onClick={() => setShowSemplice(false)} className="p-1 hover:bg-slate-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea rows={12} value={testoSemplice}
              onChange={(e) => setTestoSemplice(e.target.value)}
              className="w-full rounded border-2 border-slate-200 p-2 text-sm" />
            <div className="flex gap-2 mt-2">
              <Button className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300"
                onClick={() => copyToClipboard(testoSemplice)}>Copia</Button>
              <Button className="flex-1 bg-brand text-white hover:bg-brand/90"
                onClick={() => { playTTS("semplice", testoSemplice); }}>
                <Volume2 className="w-4 h-4 mr-1" /> Ascolta
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Dialog stima costi DEI */}
      {showStima && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => setShowStima(false)}>
          <Card className="w-full max-w-lg p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="ai-stima-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg">Stima costi (prezzario DEI)</div>
              <button onClick={() => setShowStima(false)} className="p-1 hover:bg-slate-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs text-slate-500 mb-1">
              Descrivi i lavori da stimare (lascia vuoto per usare le note del sopralluogo)
            </div>
            <textarea data-testid="ai-stima-desc" rows={3} value={stimaDesc}
              onChange={(e) => setStimaDesc(e.target.value)}
              placeholder="Es. Rifacimento di 20 mq di intonaco esterno con tinteggiatura"
              className="w-full rounded border-2 border-slate-200 p-2 text-sm mb-2" />
            <Button data-testid="ai-stima-run" onClick={generaStima}
              disabled={loadingStima}
              className="w-full bg-brand text-white hover:bg-brand/90 mb-2">
              {loadingStima ? <Loader2 className="w-4 h-4 animate-spin" /> : "Genera stima"}
            </Button>
            {stimaTesto && (
              <div data-testid="ai-stima-result" className="bg-slate-50 rounded-lg p-2 text-xs whitespace-pre-wrap font-mono border">
                {stimaTesto}
              </div>
            )}
            {stimaTesto && (
              <Button size="sm" onClick={() => copyToClipboard(stimaTesto)}
                className="mt-2 h-7 text-xs bg-slate-700 text-white hover:bg-slate-800">
                Copia stima
              </Button>
            )}
          </Card>
        </div>
      )}

      {/* Iter123k231 · Dialog "Collega condominio" (sopralluoghi liberi senza condominio_id) */}
      {showLinkCond && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => !linkCondSaving && setShowLinkCond(false)}
          data-testid="copilot-link-cond-dialog">
          <Card className="w-full max-w-md p-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-600" /> Collega a un condominio
              </div>
              <button onClick={() => !linkCondSaving && setShowLinkCond(false)}
                className="p-1 hover:bg-slate-100 rounded-full"
                data-testid="copilot-link-cond-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs text-slate-600 mb-3 leading-snug">
              Il nome &laquo;<b>{sess?.condominio_nome || "—"}</b>&raquo; non è associato a un condominio in anagrafica.
              Seleziona quello corretto dall&apos;elenco qui sotto: una volta collegato potrai affidare l&apos;incarico ai fornitori.
            </div>
            <Input data-testid="copilot-link-cond-filter"
              value={linkCondFilter}
              onChange={(e) => setLinkCondFilter(e.target.value)}
              placeholder="Filtra per nome o indirizzo…"
              className="h-10 mb-2" />
            {linkCondLoading ? (
              <div className="py-6 text-center text-sm text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin inline-block mb-1" />
                <div>Caricamento condomini…</div>
              </div>
            ) : (
              <div className="border-2 border-slate-200 rounded-lg max-h-72 overflow-y-auto mb-3"
                data-testid="copilot-link-cond-list">
                {(() => {
                  const q = linkCondFilter.toLowerCase().trim();
                  const items = (linkCondList || []).filter((c) => {
                    if (!q) return true;
                    return (c.nome || "").toLowerCase().includes(q) ||
                      (c.indirizzo || "").toLowerCase().includes(q);
                  });
                  if (items.length === 0) {
                    return (
                      <div className="p-3 text-xs text-slate-400 italic">
                        Nessun condominio trovato. Verifica l&apos;anagrafica Condomini.
                      </div>
                    );
                  }
                  return items.map((c) => (
                    <label key={c.id}
                      data-testid={`copilot-link-cond-opt-${c.id}`}
                      className={`flex items-start gap-2 p-2 cursor-pointer border-b last:border-b-0 ${
                        linkCondSel === c.id ? "bg-amber-50" : "hover:bg-slate-50"
                      }`}>
                      <input type="radio" name="linkCond" checked={linkCondSel === c.id}
                        onChange={() => setLinkCondSel(c.id)}
                        className="mt-1 accent-amber-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{c.nome}</div>
                        {c.indirizzo && <div className="text-[11px] text-slate-500 truncate">{c.indirizzo}</div>}
                      </div>
                    </label>
                  ));
                })()}
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setShowLinkCond(false)}
                disabled={linkCondSaving}
                className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300">
                Annulla
              </Button>
              <Button data-testid="copilot-link-cond-submit"
                onClick={submitLinkCondominio}
                disabled={linkCondSaving || !linkCondSel}
                className="flex-[2] bg-amber-600 hover:bg-amber-700 text-white">
                {linkCondSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Briefcase className="w-4 h-4 mr-1" />}
                Collega condominio
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Iter123k230 · Dialog "Affida incarico a Fornitore" */}
      {showAffida && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => !affidaSending && setShowAffida(false)}
          data-testid="copilot-affida-dialog">
          <Card className="w-full max-w-2xl p-4 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-600" /> Affida incarico a Fornitore
              </div>
              <button onClick={() => !affidaSending && setShowAffida(false)}
                className="p-1 hover:bg-slate-100 rounded-full"
                data-testid="copilot-affida-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {affidaResult ? (
              /* Risultato */
              <div data-testid="copilot-affida-result">
                <div className="flex items-center gap-2 text-emerald-700 mb-3">
                  <CheckCircle2 className="w-6 h-6" />
                  <div>
                    <div className="font-bold">Incarico affidato</div>
                    <div className="text-xs text-slate-600">
                      {affidaResult.tickets?.length || 0} manutenzion{(affidaResult.tickets?.length || 0) === 1 ? "e creata" : "i create"}
                      {" · "}email inviate: {affidaResult.inviati || 0}
                    </div>
                  </div>
                </div>
                <div className="border-2 border-emerald-200 rounded-lg p-2 bg-emerald-50 mb-3">
                  {(affidaResult.tickets || []).map((t) => (
                    <div key={t.ticket_id} className="flex items-center justify-between py-1 border-b last:border-b-0 border-emerald-100">
                      <div className="text-xs">
                        <div className="font-semibold">{t.numero} · {t.fornitore_nome}</div>
                        <div className="text-slate-500">{t.email || "(nessuna email)"}</div>
                      </div>
                      <Button size="sm" variant="outline"
                        onClick={() => navigate(`/manutenzioni/${t.ticket_id}`)}
                        data-testid={`copilot-affida-goto-${t.ticket_id}`}
                        className="h-7 text-xs">
                        Apri manutenzione
                      </Button>
                    </div>
                  ))}
                </div>
                {(affidaResult.non_inviati || []).length > 0 && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                    <b>Attenzione:</b> {affidaResult.non_inviati.length} fornitore/i senza email o con invio fallito.
                    La manutenzione è stata comunque creata.
                  </div>
                )}
                <Button data-testid="copilot-affida-done"
                  onClick={() => setShowAffida(false)}
                  className="w-full bg-brand text-white hover:bg-brand/90">
                  Chiudi
                </Button>
              </div>
            ) : affidaLoading ? (
              <div className="py-8 text-center text-sm text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin inline-block mb-2" />
                <div>Caricamento anagrafiche…</div>
              </div>
            ) : (
              <>
                {/* Tipo incarico */}
                <div className="text-xs font-bold uppercase text-slate-500 mb-2">
                  Tipologia incarico
                </div>
                <div className="flex gap-2 mb-3">
                  {[
                    { v: "ispettivo", l: "Sopralluogo ispettivo", d: "Verifica/perizia sul luogo" },
                    { v: "riparatorio", l: "Intervento riparatorio", d: "Esecuzione dei lavori" },
                  ].map((o) => (
                    <button key={o.v}
                      data-testid={`copilot-affida-tipo-${o.v}`}
                      onClick={() => setAffidaTipo(o.v)}
                      className={`flex-1 text-left rounded-lg border-2 p-2 transition ${
                        affidaTipo === o.v
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}>
                      <div className="font-bold text-sm">{o.l}</div>
                      <div className="text-[10px] text-slate-500">{o.d}</div>
                    </button>
                  ))}
                </div>

                {/* Priorità */}
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">
                  Priorità manutenzione
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {["Bassa", "Media", "Alta", "Critica"].map((p) => (
                    <button key={p}
                      data-testid={`copilot-affida-pri-${p}`}
                      onClick={() => setAffidaPriorita(p)}
                      className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${
                        affidaPriorita === p
                          ? "bg-brand text-white border-brand"
                          : "border-slate-300 text-slate-600"
                      }`}>
                      {p}
                    </button>
                  ))}
                </div>

                {/* Fornitori */}
                {(() => {
                  const q = affidaForntFilter.trim().toLowerCase();
                  const matches = (affidaFornitori || []).filter((f) => {
                    if (!q) return true;
                    return (
                      (f.ragione_sociale || "").toLowerCase().includes(q) ||
                      (f.email || "").toLowerCase().includes(q) ||
                      (f.settore || "").toLowerCase().includes(q) ||
                      (f.categorie || []).some((c) => (c || "").toLowerCase().includes(q))
                    );
                  });
                  const visibleIds = new Set(matches.map((f) => f.id));
                  const allVisibleSel = matches.length > 0 && matches.every((f) => affidaForntSel.has(f.id));
                  return (
                    <>
                      <div className="text-xs font-bold uppercase text-slate-500 mb-1 flex items-center justify-between">
                        <span>Fornitori ({affidaForntSel.size}/{affidaFornitori.length})</span>
                        {matches.length > 0 && (
                          <button
                            data-testid="copilot-affida-forn-selall"
                            onClick={() => {
                              const n = new Set(affidaForntSel);
                              if (allVisibleSel) matches.forEach((f) => n.delete(f.id));
                              else matches.forEach((f) => n.add(f.id));
                              setAffidaForntSel(n);
                            }}
                            className="text-brand hover:underline normal-case">
                            {allVisibleSel ? "Deseleziona visibili" : "Seleziona visibili"}
                          </button>
                        )}
                      </div>
                      <Input
                        data-testid="copilot-affida-forn-filter"
                        value={affidaForntFilter}
                        onChange={(e) => setAffidaForntFilter(e.target.value)}
                        placeholder="Cerca fornitore per nome, email o categoria…"
                        className="h-9 mb-2 text-sm" />
                      <div className="border-2 border-slate-200 rounded-lg max-h-40 overflow-y-auto mb-3"
                        data-testid="copilot-affida-fornitori-list">
                        {affidaFornitori.length === 0 ? (
                          <div className="p-3 text-xs text-slate-400 italic">
                            Nessun fornitore in anagrafica. Aggiungine prima uno nella sezione Fornitori.
                          </div>
                        ) : matches.length === 0 ? (
                          <div className="p-3 text-xs text-slate-400 italic">
                            Nessun fornitore corrisponde al filtro &laquo;{affidaForntFilter}&raquo;.
                          </div>
                        ) : matches.map((f) => (
                          <label key={f.id}
                            data-testid={`copilot-affida-forn-${f.id}`}
                            className={`flex items-start gap-2 p-2 cursor-pointer border-b last:border-b-0 ${
                              affidaForntSel.has(f.id) ? "bg-amber-50" : "hover:bg-slate-50"
                            }`}>
                            <input type="checkbox" checked={affidaForntSel.has(f.id)}
                              onChange={() => toggleAffidaForn(f.id)}
                              className="mt-1 accent-amber-600" />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm truncate">{f.ragione_sociale}</div>
                              <div className="text-[10px] text-slate-500 truncate">
                                {(f.categorie || []).join(", ") || f.settore || "—"}
                                {f.email && ` · ${f.email}`}
                                {!f.email && <span className="text-red-500"> · nessuna email</span>}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                      {/* Preserva i selezionati fuori dai risultati visibili */}
                      {affidaForntSel.size > 0 && affidaForntSel.size !== visibleIds.size && (
                        <div className="text-[10px] text-slate-500 -mt-2 mb-3">
                          {Array.from(affidaForntSel).filter((id) => !visibleIds.has(id)).length} selezionati fuori filtro
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Soggetti coinvolti */}
                {(() => {
                  const q = affidaSoggFilter.trim().toLowerCase();
                  const matches = (affidaSoggetti || []).filter((s) => {
                    if (!q) return true;
                    return (
                      (s.nome || "").toLowerCase().includes(q) ||
                      (s.email || "").toLowerCase().includes(q) ||
                      (s.telefono || "").toLowerCase().includes(q) ||
                      (s.unita_interno || "").toString().toLowerCase().includes(q)
                    );
                  });
                  const sidOf = (s) => s.user_id || s.soggetto_id;
                  const visibleIds = new Set(matches.map(sidOf));
                  const allVisibleSel = matches.length > 0 && matches.every((s) => affidaSoggSel.has(sidOf(s)));
                  return (
                    <>
                      <div className="text-xs font-bold uppercase text-slate-500 mb-1 flex items-center justify-between">
                        <span>Condòmini coinvolti ({affidaSoggSel.size}/{affidaSoggetti.length})</span>
                        {matches.length > 0 && (
                          <button
                            data-testid="copilot-affida-sogg-selall"
                            onClick={() => {
                              const n = new Set(affidaSoggSel);
                              if (allVisibleSel) matches.forEach((s) => n.delete(sidOf(s)));
                              else matches.forEach((s) => n.add(sidOf(s)));
                              setAffidaSoggSel(n);
                            }}
                            className="text-brand hover:underline normal-case">
                            {allVisibleSel ? "Deseleziona visibili" : "Seleziona visibili"}
                          </button>
                        )}
                      </div>
                      <Input
                        data-testid="copilot-affida-sogg-filter"
                        value={affidaSoggFilter}
                        onChange={(e) => setAffidaSoggFilter(e.target.value)}
                        placeholder="Cerca condòmino per nome, email, telefono o interno…"
                        className="h-9 mb-2 text-sm" />
                      <div className="border-2 border-slate-200 rounded-lg max-h-40 overflow-y-auto mb-3"
                        data-testid="copilot-affida-soggetti-list">
                        {affidaSoggetti.length === 0 ? (
                          <div className="p-3 text-xs text-slate-400 italic">
                            Nessun condòmino collegato al condominio.
                          </div>
                        ) : matches.length === 0 ? (
                          <div className="p-3 text-xs text-slate-400 italic">
                            Nessun condòmino corrisponde al filtro &laquo;{affidaSoggFilter}&raquo;.
                          </div>
                        ) : matches.map((s) => {
                          const sid2 = sidOf(s);
                          return (
                            <label key={sid2}
                              data-testid={`copilot-affida-sogg-${sid2}`}
                              className={`flex items-start gap-2 p-2 cursor-pointer border-b last:border-b-0 ${
                                affidaSoggSel.has(sid2) ? "bg-amber-50" : "hover:bg-slate-50"
                              }`}>
                              <input type="checkbox" checked={affidaSoggSel.has(sid2)}
                                onChange={() => toggleAffidaSogg(sid2)}
                                className="mt-1 accent-amber-600" />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">
                                  {s.nome}
                                  {s.unita_interno && <span className="text-slate-400 font-normal"> · int. {s.unita_interno}</span>}
                                  {s.consigliere && <span className="ml-1 text-[10px] text-amber-700 bg-amber-100 px-1 rounded">consigliere</span>}
                                </div>
                                <div className="text-[10px] text-slate-500 truncate">
                                  {s.email || "no email"}{s.telefono && ` · ${s.telefono}`}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {affidaSoggSel.size > 0 && affidaSoggSel.size !== visibleIds.size && (
                        <div className="text-[10px] text-slate-500 -mt-2 mb-3">
                          {Array.from(affidaSoggSel).filter((id) => !visibleIds.has(id)).length} selezionati fuori filtro
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Iter123k233 · Inoltro opzionale ai condòmini selezionati */}
                {(() => {
                  const soggConEmail = (affidaSoggetti || []).filter(
                    (s) => affidaSoggSel.has(s.user_id || s.soggetto_id) && (s.email || "").trim(),
                  ).length;
                  const soggSenzaEmail = affidaSoggSel.size - soggConEmail;
                  return (
                    <div className={`rounded-lg border-2 p-3 mb-3 ${affidaInoltraCond ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox"
                          data-testid="copilot-affida-inoltra-cond"
                          checked={affidaInoltraCond}
                          onChange={(e) => setAffidaInoltraCond(e.target.checked)}
                          disabled={affidaSoggSel.size === 0}
                          className="mt-1 accent-amber-600" />
                        <div className="flex-1 text-xs leading-snug">
                          <div className="font-semibold text-slate-800">
                            Inoltra lo stesso report anche ai condòmini selezionati
                            {affidaSoggSel.size > 0 && (
                              <span className="ml-1 text-slate-500 font-normal">
                                ({soggConEmail} con email{soggSenzaEmail > 0 ? `, ${soggSenzaEmail} senza` : ""})
                              </span>
                            )}
                          </div>
                          {affidaSoggSel.size === 0 && (
                            <div className="text-slate-500 mt-1 italic">Seleziona prima almeno un condòmino per abilitare questa opzione.</div>
                          )}
                          {affidaInoltraCond && (
                            <div className="mt-2 text-emerald-900 bg-emerald-50 border border-emerald-300 rounded p-2 flex items-start gap-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>
                                <b>Variante GDPR-safe</b>: ai condòmini verrà inviata una
                                versione dedicata del report che <b>NON contiene i dati di
                                contatto degli altri condòmini</b> coinvolti.
                              </span>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  );
                })()}

                {/* Note */}
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">
                  Note aggiuntive per il fornitore (opzionali)
                </div>
                <textarea data-testid="copilot-affida-note"
                  rows={3}
                  value={affidaNote}
                  onChange={(e) => setAffidaNote(e.target.value)}
                  placeholder="Es. accesso lato cortile, chiavi dal portiere, intervento entro 48h…"
                  className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm mb-3" />

                {/* Hint */}
                <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2 mb-3">
                  Verrà creata una <b>manutenzione</b> per ciascun fornitore in stato
                  <b> &laquo;Affidata&raquo;</b>, con allegato il PDF del sopralluogo
                  (versione fornitore, <b>senza</b> la tabella durata/costo).
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => setShowAffida(false)}
                    disabled={affidaSending}
                    className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300">
                    Annulla
                  </Button>
                  <Button data-testid="copilot-affida-submit"
                    onClick={submitAffida}
                    disabled={affidaSending || affidaForntSel.size === 0}
                    className="flex-[2] bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90 text-white">
                    {affidaSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Briefcase className="w-4 h-4 mr-1" />}
                    Crea manutenzione e invia report
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Dialog share — Iter123k254 · lista arricchita destinatari */}
      {sharing && (
        <div className="fixed inset-0 bg-black/50 z-30 flex items-end sm:items-center justify-center p-4" onClick={() => setSharing(false)}>
          <Card className="w-full max-w-md p-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="copilot-share-dialog">
            <div className="font-bold text-lg mb-3">Condividi report</div>

            <div className="overflow-y-auto flex-1 pr-1 -mr-1 space-y-3">
              {/* Chip categorie (invio a TUTTI della categoria) */}
              <div>
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">Invia a tutta la categoria</div>
                <div className="flex gap-2 flex-wrap">
                  {["fornitori", "condomini", "consiglieri"].map((c) => (
                    <button key={c} data-testid={`copilot-share-cat-${c}`} onClick={() => toggleCat(c)}
                      className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${shareCats.has(c) ? "bg-brand text-white border-brand" : "border-slate-300 text-slate-600"}`}>
                      {c}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  Clic su un chip = invia a tutti i soggetti collegati al condominio.
                </div>
              </div>

              {/* Liste destinatari per selezione puntuale */}
              {shareListaLoading && (
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carico i destinatari…
                </div>
              )}

              {!shareListaLoading && ["fornitori", "condomini", "consiglieri"].map((cat) => {
                const items = shareLista[cat] || [];
                if (!items.length) return null;
                const sel = shareSoggSel[cat] || new Set();
                const allSel = items.length > 0 && sel.size === items.length;
                return (
                  <div key={cat} data-testid={`copilot-share-list-${cat}`} className="border-2 border-slate-200 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-bold uppercase text-slate-600">
                        {cat} <span className="text-slate-400">({items.length})</span>
                      </div>
                      <button data-testid={`copilot-share-all-${cat}`} type="button" onClick={() => toggleAllCat(cat)}
                        className="text-[11px] text-brand font-semibold">
                        {allSel ? "Deseleziona tutti" : "Seleziona tutti"}
                      </button>
                    </div>
                    <div className="max-h-44 overflow-y-auto divide-y divide-slate-100">
                      {items.map((it) => (
                        <label key={it.id} className="flex items-start gap-2 py-1.5 text-xs cursor-pointer">
                          <input type="checkbox" checked={sel.has(it.id)}
                            onChange={() => toggleSogg(cat, it.id)}
                            data-testid={`copilot-share-sogg-${cat}-${it.id}`}
                            className="mt-0.5 accent-brand" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-800 truncate">{it.nome}</div>
                            <div className="text-[10px] text-slate-500 truncate">
                              {it.email}
                              {cat !== "fornitori" && (it.codice_unita || it.scala) && (
                                <> · <span className="font-mono">
                                  {it.scala ? `sc. ${it.scala}` : ""}
                                  {it.scala && it.codice_unita ? " · " : ""}
                                  {it.codice_unita ? `u. ${it.codice_unita}` : ""}
                                </span></>
                              )}
                              {cat === "fornitori" && it.categoria && <> · {it.categoria}</>}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

              {!shareListaLoading && !shareLista.fornitori.length && !shareLista.condomini.length && !shareLista.consiglieri.length && (
                <div className="text-[11px] text-slate-500 italic">
                  Nessun soggetto collegato al condominio della sessione. Puoi comunque inviare a email libere qui sotto.
                </div>
              )}

              <div>
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">Email aggiuntive (virgola)</div>
                <input data-testid="copilot-share-emails" value={shareEmails}
                  onChange={(e) => setShareEmails(e.target.value)}
                  placeholder="mario@esempio.it, …"
                  className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm" />
              </div>
            </div>

            <div className="flex gap-2 mt-3 pt-2 border-t border-slate-200">
              <Button onClick={() => setSharing(false)} className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300">Annulla</Button>
              <Button data-testid="copilot-share-submit" onClick={inviaReport} className="flex-1 bg-brand text-white hover:bg-brand/90">Invia</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Iter123k243 · Dialog riapri sessione */}
      {showReopen && (
        <div className="fixed inset-0 bg-black/60 z-40 flex items-end sm:items-center justify-center p-3"
          onClick={() => !reopenSaving && setShowReopen(false)}>
          <Card className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}
            data-testid="copilot-riapri-dialog">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg flex items-center gap-2">
                <Play className="w-5 h-5 text-sky-600" /> Riapri sopralluogo
              </div>
              <button onClick={() => !reopenSaving && setShowReopen(false)}
                className="p-1 hover:bg-slate-100 rounded-full"
                data-testid="copilot-riapri-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs text-slate-600 leading-snug mb-3">
              Il sopralluogo tornerà in stato <b>in corso</b>: il cronometro ripartirà
              da zero e la durata appena registrata si sommerà alla precedente
              (<b>{fmtMin(sess?.durata_min || 0)}</b>). L&apos;evento viene annotato nella cronologia.
            </div>
            <div className="text-xs font-semibold uppercase text-slate-500 mb-1">
              Motivo (facoltativo)
            </div>
            <textarea data-testid="copilot-riapri-motivo" rows={3}
              value={reopenMotivo} onChange={(e) => setReopenMotivo(e.target.value)}
              placeholder="Es. ritorno in cantina per fotografare la nuova infiltrazione"
              className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm mb-3" />
            <div className="flex gap-2">
              <Button onClick={() => setShowReopen(false)} disabled={reopenSaving}
                className="flex-1 bg-slate-200 text-slate-800 hover:bg-slate-300">
                Annulla
              </Button>
              <Button data-testid="copilot-riapri-confirm" onClick={confirmReopen}
                disabled={reopenSaving}
                className="flex-1 bg-sky-600 hover:bg-sky-700 text-white">
                {reopenSaving
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Play className="w-4 h-4 mr-1" />}
                Riapri
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
